import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, Search, ShoppingBag, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useStore } from "@/store/StoreContext";

const NAV = [
  { to: "/", label: "Beranda" },
  { to: "/koleksi", label: "Koleksi" },
  { to: "/custom", label: "Custom Order" },
  { to: "/tentang-kami", label: "Tentang Kami" },
  { to: "/kontak", label: "Kontak" },
];

export const Header = () => {
  const { cartCount, wishlist, setSearchOpen } = useStore();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className="sticky top-0 z-40 border-b border-line bg-paper/95 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 md:px-10 lg:px-16">
        <button
          className="-ml-2 p-2 text-ink md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>

        <Link to="/" className="group text-ink" data-testid="brand-logo">
          <Logo subline="Beads" markSize={32} />
        </Link>

        <nav className="hidden items-center gap-10 md:flex" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `link-underline text-[13px] tracking-[0.08em] transition-colors duration-300 ${
                  isActive ? "text-ink" : "text-stone hover:text-ink"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2 text-ink transition-colors duration-300 hover:text-sand"
            aria-label="Cari"
            data-testid="header-search-button"
          >
            <Search size={19} strokeWidth={1.5} />
          </button>
          <Link
            to="/wishlist"
            className="relative p-2 text-ink transition-colors duration-300 hover:text-sand"
            aria-label="Favorit"
            data-testid="header-wishlist-link"
          >
            <Heart size={19} strokeWidth={1.5} />
            {wishlist.length > 0 && (
              <span
                className="absolute right-0 top-0 flex h-[16px] min-w-[16px] items-center justify-center bg-sand px-[4px] text-[10px] font-semibold text-paper"
                data-testid="wishlist-count"
              >
                {wishlist.length}
              </span>
            )}
          </Link>
          <Link
            to="/keranjang"
            className="relative p-2 text-ink transition-colors duration-300 hover:text-sand"
            aria-label="Daftar pilihan"
            data-testid="header-cart-link"
          >
            <ShoppingBag size={19} strokeWidth={1.5} />
            {cartCount > 0 && (
              <span
                className="absolute right-0 top-0 flex h-[16px] min-w-[16px] items-center justify-center bg-ink px-[4px] text-[10px] font-semibold text-paper"
                data-testid="cart-count"
              >
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </div>

      {open && (
        <nav
          className="border-t border-line bg-linen px-5 py-6 md:hidden"
          data-testid="mobile-nav"
        >
          <ul className="flex flex-col gap-5">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="font-display text-2xl text-ink"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
};
